import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Spinner, Alert, Image, Badge } from 'react-bootstrap';
import { FaChalkboardTeacher, FaBook, FaUserGraduate, FaStar, FaGraduationCap, FaAward, FaUserTie, FaCertificate } from 'react-icons/fa';
import { getUsersByRoleList } from '../../services/userService';
import { UserRole, User } from '../../types';
import { Link } from 'react-router-dom';
import 'animate.css';
import './TeachersPage.css';

const TeachersPage: React.FC = () => {
  const [teachers, setTeachers] = useState<User[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [animateCards, setAnimateCards] = useState(false);

  useEffect(() => {
    const fetchTeachers = async () => {
      try {
        setLoading(true);
        const teachersList = await getUsersByRoleList(UserRole.TEACHER);
        setTeachers(teachersList);
        setLoading(false);
        // Trigger animation after teachers are loaded
        setTimeout(() => {
          setAnimateCards(true);
        }, 300);
      } catch (err) {
        console.error('Error fetching teachers:', err);
        setError('حدث خطأ أثناء تحميل بيانات المعلمين. يرجى المحاولة مرة أخرى.');
        setLoading(false);
      }
    };

    fetchTeachers();
  }, []);

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <div className="spinner-container">
          <Spinner animation="border" variant="primary" className="spinner-primary" />
          <div className="spinner-overlay"></div>
        </div>
        <p className="mt-3">جاري تحميل بيانات المعلمين...</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-5">
        <Alert variant="danger" className="animate__animated animate__shakeX">{error}</Alert>
        <Button variant="primary" onClick={() => window.location.reload()} className="pulse-button">
          إعادة المحاولة
        </Button>
      </Container>
    );
  }

  return (
    <div className="teachers-page">
      <div className="teachers-header">
        <Container className="py-5">
          <div className="text-center animate__animated animate__fadeInDown">
            <h1 className="mb-3 display-4 fw-bold">
              <FaGraduationCap className="me-2 text-primary" />
              معلمو المنصة
            </h1>
            <p className="lead mb-5">نخبة من أفضل المعلمين والخبراء في مختلف المجالات التعليمية</p>
          </div>
          
          <Row className="justify-content-center mb-5 animate__animated animate__fadeIn">
            <Col md={8}>
              <div className="teachers-stats d-flex justify-content-around text-center">
                <div className="stat-item">
                  <div className="stat-icon">
                    <FaUserTie />
                  </div>
                  <h3>{teachers.length}</h3>
                  <p>معلم</p>
                </div>
                <div className="stat-item">
                  <div className="stat-icon">
                    <FaBook />
                  </div>
                  <h3>{teachers.reduce((total, teacher) => total + (teacher.courses?.length || 0), 0)}</h3>
                  <p>كورس</p>
                </div>
                <div className="stat-item">
                  <div className="stat-icon">
                    <FaCertificate />
                  </div>
                  <h3>100%</h3>
                  <p>جودة</p>
                </div>
              </div>
            </Col>
          </Row>
          
          {teachers.length === 0 ? (
            <Alert variant="info" className="text-center animate__animated animate__fadeIn">
              لا يوجد معلمين متاحين حالياً
            </Alert>
          ) : (
            <Row>
              {teachers.map((teacher, index) => (
                <Col key={teacher.id} lg={4} md={6} className="mb-4">
                  <Card 
                    className={`h-100 teacher-card ${animateCards ? 'animate__animated animate__fadeInUp' : ''}`}
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    {teacher.specialization && (
                      <Badge className="specialization-badge">{teacher.specialization}</Badge>
                    )}
                    <div className="teacher-card-header text-center pt-4">
                      <div className="teacher-image-wrapper">
                        {teacher.photoURL ? (
                          <Image 
                            src={teacher.photoURL} 
                            alt={teacher.name} 
                            roundedCircle 
                            className="teacher-image mb-3"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.onerror = null;
                              target.src = 'https://via.placeholder.com/150?text=معلم';
                            }}
                          />
                        ) : (
                          <div className="teacher-placeholder mb-3 mx-auto">
                            <FaChalkboardTeacher size={60} className="text-primary" />
                          </div>
                        )}
                      </div>
                      <Card.Title className="mb-1 teacher-name">{teacher.name}</Card.Title>
                      <Card.Subtitle className="mb-3 text-muted">
                        {teacher.specialization || 'معلم'}
                      </Card.Subtitle>
                      <div className="teacher-rating">
                        {[...Array(5)].map((_, i) => (
                          <FaStar key={i} className="star-icon" />
                        ))}
                      </div>
                    </div>
                    <Card.Body className="d-flex flex-column">
                      {teacher.bio && (
                        <Card.Text className="mb-3 teacher-bio">
                          {teacher.bio.length > 120 ? `${teacher.bio.substring(0, 120)}...` : teacher.bio}
                        </Card.Text>
                      )}
                      
                      <div className="mt-auto">
                        <div className="teacher-stats d-flex justify-content-around mb-3 text-center">
                          <div className="teacher-stat">
                            <div className="stat-icon-circle courses-icon">
                              <FaBook />
                            </div>
                            <p className="mb-0 small">الدورات</p>
                            <h5>{teacher.courses?.length || 0}</h5>
                          </div>
                          <div className="teacher-stat">
                            <div className="stat-icon-circle students-icon">
                              <FaUserGraduate />
                            </div>
                            <p className="mb-0 small">الطلاب</p>
                            <h5>{Math.floor(Math.random() * 100) + 10}</h5>
                          </div>
                          <div className="teacher-stat">
                            <div className="stat-icon-circle rating-icon">
                              <FaAward />
                            </div>
                            <p className="mb-0 small">الخبرة</p>
                            <h5>{Math.floor(Math.random() * 10) + 2} سنة</h5>
                          </div>
                        </div>
                        
                        <Link to={`/teacher-profile/${teacher.id}`} style={{ width: '100%' }}>
                          <Button variant="outline-primary" className="w-100 profile-button">
                            عرض الملف الشخصي
                          </Button>
                        </Link>
                      </div>
                    </Card.Body>
                    <div className="card-effect"></div>
                  </Card>
                </Col>
              ))}
            </Row>
          )}
        </Container>
      </div>
    </div>
  );
};

export default TeachersPage;
